import { format, parseISO } from 'date-fns';

import { DeadlineColumnValue } from './types';

export type DeadlineFilterValue = 'overdue' | 'upcoming' | 'completed_on_time' | 'none';

type DeadlineDisplay = {
  primaryText: string;
  secondaryText?: string;
  title: string;
  showOverdueDot?: boolean;
};

export const DEADLINE_FILTER_OPTIONS: { label: string; value: DeadlineFilterValue }[] = [
  { label: 'Overdue', value: 'overdue' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Completed on time', value: 'completed_on_time' },
  { label: 'None', value: 'none' },
];

export const ALL_DEADLINE_FILTER_VALUES = DEADLINE_FILTER_OPTIONS.map(({ value }) => value);

export const DEADLINE_COMPLETED_STATUSES = new Set<NonNullable<DeadlineColumnValue>['status']>([
  'completed',
  'completed_on_time',
  'completed_overdue',
]);

const getDateOnlyTimestamp = (value: string): number => {
  const date = parseISO(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const resolveDayCount = (deadline: DeadlineColumnValue): number | null => {
  if (typeof deadline?.days_remaining === 'number') {
    return Math.abs(deadline.days_remaining);
  }

  if (!deadline?.due_date) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffInMs = getDateOnlyTimestamp(deadline.due_date) - today.getTime();
  return Math.abs(Math.round(diffInMs / (1000 * 60 * 60 * 24)));
};

const formatDayLabel = (count: number, suffix: string): string =>
  `${count} day${count === 1 ? '' : 's'} ${suffix}`;

export const formatDeadlineDate = (dueDate: string): string =>
  format(parseISO(dueDate), 'MMM dd, yyyy');

export const formatDeadlineDateNoLeadingZero = (dueDate: string): string =>
  format(parseISO(dueDate), 'MMM d, yyyy');

export const getDeadlineDisplay = (deadline: DeadlineColumnValue): DeadlineDisplay => {
  if (!deadline?.due_date || deadline.status === 'none') {
    return { primaryText: 'No deadline', title: 'No deadline' };
  }

  const formattedDate = formatDeadlineDate(deadline.due_date);
  const dayCount = resolveDayCount(deadline);

  switch (deadline.status) {
    case 'upcoming':
      return dayCount === null
        ? { primaryText: formattedDate, title: formattedDate }
        : {
            primaryText: formattedDate,
            secondaryText: formatDayLabel(dayCount, 'remaining'),
            title: `${formattedDate} ${formatDayLabel(dayCount, 'remaining')}`,
          };
    case 'overdue':
      return dayCount === null
        ? { primaryText: formattedDate, title: formattedDate }
        : {
            primaryText: formattedDate,
            secondaryText: formatDayLabel(dayCount, 'overdue'),
            title: `${formattedDate} ${formatDayLabel(dayCount, 'overdue')}`,
            showOverdueDot: true,
          };
    case 'completed_on_time':
    case 'completed':
      return {
        primaryText: formattedDate,
        secondaryText: 'Completed',
        title: `${formattedDate} Completed`,
      };
    case 'completed_overdue':
      return {
        primaryText: formattedDate,
        secondaryText: 'Completed (late)',
        title: `${formattedDate} Completed (late)`,
      };
    default:
      return { primaryText: 'No deadline', title: 'No deadline' };
  }
};
